import { useTranslation } from "react-i18next";
import { ExternalLink as ArrowOutIcon } from "lucide-react";
import { ChannelIdentityBadge } from "../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../components/channel/channel-presentation.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import type { ContactChannelIdentity } from "./conversation-contact-identities.js";
import { isPhoneLikeDisplay } from "./inbox-display.js";

/**
 * One channel profile of a saved contact with a way into its conversation. `details` is the
 * compact Inbox Details row; `lookup` is the bordered card inside the contact lookup dialog.
 */
export function ContactChannelIdentityRow({
  identity,
  variant,
  onOpenConversation,
}: {
  identity: ContactChannelIdentity;
  variant: "details" | "lookup";
  onOpenConversation: (conversationId: number) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const channelLabel = t(`channel.${channelLabelI18nKey(identity.platformType)}`);
  const lookup = variant === "lookup";
  const value = identity.identity ?? channelLabel;
  // The value line has its own (LTR or auto) direction; align it to the UI side like the label.
  const valueAlignClass = i18n.dir() === "rtl" ? "text-right" : "text-left";

  return (
    <div
      className={cn(
        "flex items-center",
        lookup
          ? "my-1.5 gap-[11px] rounded-xl border border-inbox-line px-3 py-2.5"
          : "gap-2.5 py-2 [&+&]:border-t [&+&]:border-dashed [&+&]:border-inbox-line",
      )}
      data-testid="contact-channel-identity"
    >
      <ChannelIdentityBadge platformType={identity.platformType} size={lookup ? "profile" : "row"} />
      <span className={cn("min-w-0 flex-1", lookup ? "text-[0.84375rem]" : "text-[0.8125rem]")}>
        {isPhoneLikeDisplay(value) ? (
          <LtrValue className={cn("block truncate text-inbox-strong tabular-nums", valueAlignClass)}>{value}</LtrValue>
        ) : (
          <bdi className={cn("block truncate text-inbox-strong", valueAlignClass)}>{value}</bdi>
        )}
        <small className={cn("block text-inbox-muted", lookup ? "text-[0.75rem]" : "text-[0.71875rem]")}>
          {channelLabel}
        </small>
      </span>
      {identity.conversationId != null ? (
        <Button
          type="button"
          variant={lookup ? "default" : "outline"}
          size="sm"
          className={cn(
            "h-[1.875rem] shrink-0 gap-1.5 rounded-lg px-2.5 text-[0.75rem]",
            !lookup && "border-inbox-line text-inbox-strong",
          )}
          onClick={() => onOpenConversation(identity.conversationId!)}
        >
          {t("inbox.contactIdentity.openConversation")}
          {lookup ? <ArrowOutIcon size={14} className="rtl:-scale-x-100" aria-hidden="true" /> : null}
        </Button>
      ) : (
        <span className="shrink-0 text-[0.75rem] whitespace-nowrap text-[#8a9894]">
          {t("inbox.contactIdentity.noConversation")}
        </span>
      )}
    </div>
  );
}
